/* global console */
import { createSlice, current } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";
import { notify } from "../components/providers/GlobalToast";
import { isCellUnderMerge } from "../helpers/merge";

export const structureSlice = createSlice({
  name: "structure",
  initialState: {
    columns: [],
    rows: [],
    cells: [],
    merged: [],
  },
  reducers: {
    reset: (state, action) => {
      if (action.payload.columns) {
        state.columns = action.payload.columns;
      }
      if (action.payload.rows) {
        state.rows = action.payload.rows;
      }
      if (action.payload.cells) {
        state.cells = action.payload.cells;
      }
      if (action.payload.merged) {
        state.merged = action.payload.merged;
      }
    },
    clear: (state) => {
      state.cells.forEach((r) =>
        r.forEach((c) => {
          c.text = "";
          c.json = null;
        })
      );
    },
    insert: (state, action) => {
      if (action.payload.columnIndex !== undefined) {
        if (action.payload.spacer) {
          const idx = action.payload.columnIndex;
          const current = state.columns[idx];
          const prev = state.columns[idx - 1];
          const isCurrentSpacer = idx < state.columns.length && typeof current === "string" && current[0] === "s";
          const isPrevSpacer = idx - 1 >= 0 && typeof prev === "string" && prev[0] === "s";
          if ((isCurrentSpacer || isPrevSpacer) && !action.payload.force) {
            console.warn("Index is already spacer, skipping");
            return;
          }
        }

        let newColumnWidth = "*";
        if (!action.payload.spacer && action.payload.columnIndex > 0) {
          const leftColumnIndex = action.payload.columnIndex - 1;
          const leftColumn = state.columns[leftColumnIndex];
          if (typeof leftColumn !== "string" || leftColumn[0] !== "s") {
            newColumnWidth = leftColumn;
          }
        }

        state.columns.splice(action.payload.columnIndex, 0, action.payload.spacer ? "s24" : newColumnWidth);
        for (let i = 0; i < state.cells.length; i++) {
          const row = state.cells[i];
          row.splice(action.payload.columnIndex, 0, {
            id: (action.payload.newCellIds ? action.payload.newCellIds[i] : undefined) ?? uuidv4(),
            text: action.payload.spacer ? undefined : "",
          });
        }

        state.merged = state.merged.map((m) => {
          if (m.cell.y >= action.payload.columnIndex) {
            m.cell.y = m.cell.y + 1;
          } else if (m.dir === "right" && m.cell.y + m.length >= action.payload.columnIndex) {
            m.length = m.length + 1;
          }
          return m;
        });
      }

      if (action.payload.rowIndex !== undefined) {
        if (action.payload.spacer) {
          const idx = action.payload.rowIndex;
          const current = state.rows[idx];
          const prev = state.rows[idx - 1];
          const isCurrentSpacer = idx < state.rows.length && typeof current === "string" && current[0] === "s";
          const isPrevSpacer = idx - 1 >= 0 && typeof prev === "string" && prev[0] === "s";
          if (isCurrentSpacer || isPrevSpacer) {
            console.warn("Index is already spacer, skipping");
            return;
          }
        }
        state.rows.splice(action.payload.rowIndex, 0, action.payload.spacer ? "s24" : "*");
        const newRow = [];
        state.columns.forEach((_, i) => {
          newRow.push({
            id: (action.payload.newCellIds ? action.payload.newCellIds[i] : undefined) ?? uuidv4(),
            text: action.payload.spacer ? undefined : "",
          });
        });
        state.cells.splice(action.payload.rowIndex, 0, newRow);

        state.merged = state.merged.map((m) => {
          if (m.cell.x >= action.payload.rowIndex) {
            m.cell.x = m.cell.x + 1;
          } else if (m.dir === "down" && m.cell.x + m.length > action.payload.rowIndex) {
            m.length = m.length + 1;
          }
          return m;
        });
      }
    },
    remove: (state, action) => {
      if (state.columns.length > 1) {
        if (action.payload.columnIndex !== undefined) {
          if (action.payload.columnIndex === 0) {
            state.columns.shift();
            state.cells.forEach((row) => {
              row.shift();
            });
          } else {
            state.columns.splice(action.payload.columnIndex, action.payload.range ?? 1);
            state.cells.forEach((row) => {
              row.splice(action.payload.columnIndex, action.payload.range ?? 1);
            });
          }

          state.merged = state.merged
            .filter((m) => !(m.dir === "down" && m.cell.y === action.payload.columnIndex)) // remove merges in deleted column
            .map((m) => {
              if (m.cell.y > action.payload.columnIndex) {
                // reindex merges that are after the deleted column
                m.cell.y = m.cell.y - 1;
              } else if (m.dir === "right" && m.cell.y + m.length >= action.payload.columnIndex) {
                // shorten merges that overlap deleted column
                m.length = m.length - 1;
              }
              return m;
            })
            .filter((m) => m.length > 0); // remove merges that would be one cell long
        }
      }

      if (state.rows.length > 1) {
        if (action.payload.rowIndex !== undefined) {
          if (action.payload.rowIndex === 0) {
            state.rows.shift();
            state.cells.shift();
          } else {
            state.rows.splice(action.payload.rowIndex, action.payload.range ?? 1);
            state.cells.splice(action.payload.rowIndex, action.payload.range ?? 1);
          }

          // remove merges in deleted row
          state.merged = state.merged
            .filter((m) => !(m.dir === "right" && m.cell.x === action.payload.rowIndex))
            .map((m) => {
              if (m.cell.x > action.payload.rowIndex) {
                m.cell.x = m.cell.x - 1;
              } else if (m.dir === "down" && m.cell.x + m.length >= action.payload.rowIndex) {
                m.length = m.length - 1;
              }
              return m;
            })
            .filter((m) => m.length > 0);
        }
      }
    },
    updateCell: (state, action) => {
      //console.log("Updating cell with action", action);
      let { x, y } = action.payload;
      if (action.payload.id) {
        for (let i = 0; i < state.cells.length; i++) {
          const col = state.cells[i];
          for (let j = 0; j < col.length; j++) {
            const c = col[j];
            if (c.id === action.payload.id) {
              x = i;
              y = j;
              break;
            }
          }

          if (x && y) break;
        }
      } else {
        if (!state.cells[action.payload.x]) {
          state.cells[action.payload.x] = [];
        }
        if (!state.cells[action.payload.x][action.payload.y]) {
          state.cells[action.payload.x][action.payload.y] = {};
        }
      }
      state.cells[x][y].text = action.payload.text;
      state.cells[x][y].json = action.payload.json;
      console.log("Updated cell with action", action);
    },
    resize: (state, action) => {
      if (action.payload.weight <= 0) {
        console.warn("Column/row being resized to negative weight, blocking resize.");
        notify("warning", "warning.resize.negative");
        return;
      }

      if (action.payload.columnIndex !== undefined) {
        state.columns.splice(action.payload.columnIndex, 1, action.payload.weight);
      }

      if (action.payload.rowIndex !== undefined) {
        state.rows.splice(action.payload.rowIndex, 1, action.payload.weight);
      }
    },
    move: (state, action) => {
      console.log("move", action);
      const { from, to, vertical } = action.payload;
      const isSpacer = vertical ? state.columns[from][0] === "s" : state.rows[from][0] === "s";

      if (vertical) {
        state.merged = state.merged
          .map((m) => {
            const was = { ...m };
            if (m.dir === "right") {
              if (from >= m.cell.y && from <= m.cell.y + m.length && (to <= m.cell.y || to >= m.cell.y + m.length)) {
                m.length -= 1;
              } else if (from > m.cell.y + m.length && to > m.cell.y && to <= m.cell.y + m.length) {
                m.length += isSpacer ? 1 : 1;
              } else if (from < m.cell.y && to >= m.cell.y && to < m.cell.y + m.length) {
                m.length += isSpacer ? 2 : 1;
              }

              if (from < m.cell.y && to >= m.cell.y) {
                m.cell.y -= 1;
              } else if (from >= m.cell.y && to <= m.cell.y) {
                m.cell.y += 1;
              }

              if (m.cell.y + m.length > state.columns.length - 1) {
                // merged cell would be longer than Gridd dimension, lower length
                m.length = state.columns.length - 1 - m.cell.y;
              }
            } else if (m.dir === "down") {
              if (from === m.cell.y) {
                m.cell.y = to;
              } else if (to === m.cell.y) {
                m.cell.y += 1;
              }
            }

            if (state.columns[m.cell.y + m.length] && state.columns[m.cell.y + m.length][0] === "s") {
              // last col of this merged cell is spacer, shorten it by 1 more
              m.length -= 1;
            } else if (state.columns[m.cell.y] && state.columns[m.cell.y][0] === "s") {
              // first col of this merged cell is spacer, shorten by 1, move by 1
              m.cell.y += 1;
              m.length -= 1;
            }
            console.log("updating merged cell", { was, m: current(m), from, to, vertical, isSpacer });

            return m;
          })
          .filter((m) => m.length > 0);

        state.columns = reorderOneItem(state.columns, from, to);

        for (let i = 0; i < state.cells.length; i++) {
          state.cells[i] = reorderOneItem(state.cells[i], from, to);
        }
      } else {
        state.merged = state.merged
          .map((m) => {
            if (m.dir === "down") {
              if (from >= m.cell.x && from <= m.cell.x + m.length && (to <= m.cell.x || to >= m.cell.x + m.length)) {
                m.length -= 1;
              } else if (from > m.cell.x + m.length && to > m.cell.x && to <= m.cell.x + m.length) {
                m.length += isSpacer ? 1 : 1;
              } else if (from < m.cell.x && to >= m.cell.x && to < m.cell.x + m.length) {
                m.length += isSpacer ? 2 : 1;
              }

              if (from < m.cell.x && to >= m.cell.x) {
                m.cell.x -= 1;
              } else if (from >= m.cell.x && to <= m.cell.x) {
                m.cell.x += 1;
              }

              if (m.cell.x + m.length > state.rows.length - 1) {
                // merged cell would be longer than Gridd dimension, lower length
                m.length = state.rows.length - 1 - m.cell.x;
              }
            } else if (m.dir === "right") {
              if (from === m.cell.x) {
                m.cell.x = to;
              } else if (to === m.cell.x) {
                m.cell.x += 1;
              }
            }

            if (state.rows[m.cell.x + m.length] && state.rows[m.cell.x + m.length][0] === "s") {
              // last row of this merged cell is spacer, shorten it by 1 more
              m.length -= 1;
            } else if (state.rows[m.cell.x] && state.rows[m.cell.x][0] === "s") {
              // first row of this merged cell is spacer, shorten by 1, move by 1
              m.cell.x += 1;
              m.length -= 1;
            }

            console.log("updating merged cell", { m: current(m), from, to, vertical, isSpacer });

            return m;
          })
          .filter((m) => m.length > 0);

        state.rows = reorderOneItem(state.rows, from, to);
        state.cells = reorderOneItem(state.cells, from, to);
      }

      console.log("Column or row moved", { result: current(state), action });
    },
    balance: (state, action) => {
      console.log("balancing", { action, state: current(state).columns });
      if (action.payload.columns?.length > 1) {
        const weightSum = state.columns.reduce(
          (sum, c, i) => (c[0] !== "s" && action.payload.columns.includes(i) ? sum + (c === "*" ? 1 : c) : sum),
          0
        );
        const colCount = state.columns.reduce(
          (count, c, i) => (c[0] !== "s" && action.payload.columns.includes(i) ? count + 1 : count),
          0
        );
        const newWeight = weightSum / colCount;
        for (let i = 0; i < action.payload.columns.length; i++) {
          const index = action.payload.columns[i];
          if (state.columns[index][0] !== "s") {
            state.columns[action.payload.columns[i]] = newWeight;
          }
        }
        console.log("rebalanced columns", { weightSum, colCount, newWeight, result: current(state).columns });
      } else if (action.payload.rows) {
        const weightSum = state.rows.reduce(
          (sum, c, i) => (c[0] !== "s" && action.payload.rows.includes(i) ? sum + (c === "*" ? 1 : c) : sum),
          0
        );
        const rowCount = state.rows.reduce(
          (count, c, i) => (c[0] !== "s" && action.payload.rows.includes(i) ? count + 1 : count),
          0
        );
        const newWeight = weightSum / rowCount;
        for (let i = 0; i < action.payload.rows.length; i++) {
          const index = action.payload.rows[i];
          if (state.rows[index][0] !== "s") {
            state.rows[action.payload.rows[i]] = newWeight;
          }
        }
        console.log("rebalanced rows", { weightSum, rowCount, newWeight, result: current(state).rows });
      }
    },
    swap: (state, action) => {
      console.log("swapping", action);
      if (action.payload.cells?.length === 2) {
        const first = action.payload.cells[0];
        const second = action.payload.cells[1];
        // swapping two cell's content
        const tempText = state.cells[first.x][first.y].text;
        const tempJSON = state.cells[first.x][first.y].json;
        state.cells[first.x][first.y].text = state.cells[second.x][second.y].text;
        state.cells[first.x][first.y].json = state.cells[second.x][second.y].json;
        state.cells[second.x][second.y].text = tempText;
        state.cells[second.x][second.y].json = tempJSON;
      } else if (action.payload.rows?.length === 2) {
        const x1 = action.payload.rows[0];
        const x2 = action.payload.rows[1];
        const temp = state.cells[x1];
        state.cells[x1] = state.cells[x2];
        state.cells[x2] = temp;
      } else if (action.payload.columns?.length === 2) {
        const y1 = action.payload.columns[0];
        const y2 = action.payload.columns[1];
        const temp = state.cells.map((row) => row[y1]);
        state.cells = state.cells.map((row, ri) => {
          const r = [...row];
          r[y1] = r[y2];
          r[y2] = temp[ri];
          return r;
        });
      }
    },
    merge: (state, action) => {
      console.log("merging", action);
      if (action.payload.cell) {
        const { cell, length, dir } = action.payload;

        // First, remove any existing merges that would be part of this new merge
        state.merged = state.merged.filter((m) => {
          // Calculate the full dimensions of both merges
          const mEndX = m.cell.x + (m.dir === "down" ? m.length : 0);
          const mEndY = m.cell.y + (m.dir === "right" ? m.length : 0);
          const newEndX = cell.x + (dir === "down" ? length : 0);
          const newEndY = cell.y + (dir === "right" ? length : 0);

          // Check if the merges overlap
          const overlaps = !(mEndX < cell.x || m.cell.x > newEndX || mEndY < cell.y || m.cell.y > newEndY);

          // Keep merges that don't overlap
          return !overlaps;
        });

        // Add the new merge
        state.merged.push({ cell, length, dir });
      }
    },
    copyMerge: (state, action) => {
      console.log("copying merge", action);
      const { sourceRowIndex, sourceColumnIndex, targetRowIndex, targetColumnIndex } = action.payload;

      if (typeof sourceRowIndex !== "undefined") {
        // Only copy source merges whose target cell isn't already under an existing merge
        const sourceMerges = state.merged.filter(
          (m) => m.cell.x === sourceRowIndex && !isCellUnderMerge(state.merged, targetRowIndex, m.cell.y)
        );
        state.merged = [
          ...state.merged,
          ...sourceMerges.map((m) => ({
            cell: { x: targetRowIndex, y: m.cell.y },
            length: m.length,
            dir: m.dir,
          })),
        ];
        console.log("source merges", { sourceMerges });
      } else {
        // Only copy source merges whose target cell isn't already under an existing merge
        const sourceMerges = state.merged.filter(
          (m) => m.cell.y === sourceColumnIndex && !isCellUnderMerge(state.merged, m.cell.x, targetColumnIndex)
        );
        state.merged = [
          ...state.merged,
          ...sourceMerges.map((m) => ({
            cell: { x: m.cell.x, y: targetColumnIndex },
            length: m.length,
            dir: m.dir,
          })),
        ];
      }
    },
    split: (state, action) => {
      console.log("splitting", action);
      if (action.payload.cell) {
        // Find and remove all merges that include this cell
        state.merged = state.merged.filter((m) => {
          const mEndX = m.cell.x + (m.dir === "down" ? m.length : 0);
          const mEndY = m.cell.y + (m.dir === "right" ? m.length : 0);

          return !(
            action.payload.cell.x >= m.cell.x &&
            action.payload.cell.x <= mEndX &&
            action.payload.cell.y >= m.cell.y &&
            action.payload.cell.y <= mEndY
          );
        });
      }
    },
    splitAll: (state) => {
      console.log("splitting all cells to recover");
      state.merged = [];
    },
  },
});

export const {
  reset,
  clear,
  insert,
  remove,
  updateCell,
  resize,
  move,
  balance,
  swap,
  merge,
  copyMerge,
  split,
  splitAll,
} = structureSlice.actions;

export default structureSlice.reducer;

const reorderOneItem = (arr, from, to) => {
  const movedItem = arr.find((item, index) => index === from);
  const remainingItems = arr.filter((item, index) => index !== from);

  console.log("Drag and drop reordering", { remainingItems, movedItem, from, to });
  return [...remainingItems.slice(0, to), movedItem, ...remainingItems.slice(to)];
};
